import { Field, Form, Formik, useField, FieldHookConfig } from "formik";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  CardFooter,
  Col,
  Container,
  FormGroup,
  Input,
  Label,
  Row,
  InputProps,
} from "reactstrap";
import { useNavigate } from "react-router-dom";

interface SmtpConfigFormValues {
  distributor_domain: string;
  distributor_name: string;
  distributor_smtp_email: string;
  distributor_smtp_password: string;
  distributor_smtp_domain: string;
  distributor_smtp_port: string;
  distributor_smtp_protocol: string;
  distributor_smtp_from_address: string;
  encryption: "ssl" | "tls";
}

type PasswordInputProps = {
  label: string;
} & FieldHookConfig<string> &
  InputProps;

const PasswordInput = ({ label, ...props }: PasswordInputProps) => {
  const [show, setShow] = useState(false);
  const [field, meta] = useField(props);

  const toggleStyle: React.CSSProperties = {
    position: "absolute",
    top: "50%",
    right: "10px",
    transform: "translateY(-50%)",
    cursor: "pointer",
    userSelect: "none",
  };

  return (
    <FormGroup>
      <Label htmlFor={props.id || props.name}>{label}</Label>
      <div className="position-relative">
        <Input
          {...field}
          {...props}
          type={show ? "text" : "password"}
          id={props.id || props.name}
          placeholder="*********"
          invalid={meta.touched && !!meta.error}
          style={{ paddingRight: "50px" }}
        />
        <Button
          type="button"
          color="link"
          onClick={() => setShow(!show)}
          style={toggleStyle}
          className="p-0 border-0 text-decoration-none"
        >
          {show ? "Hide" : "Show"}
        </Button>
      </div>
      {meta.touched && meta.error ? (
        <div className="text-danger">{meta.error}</div>
      ) : null}
    </FormGroup>
  );
};

function SMTPConfigSection() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const initialValues: SmtpConfigFormValues = {
    distributor_domain: "",
    distributor_name: "",
    distributor_smtp_email: "",
    distributor_smtp_password: "",
    distributor_smtp_domain: "",
    distributor_smtp_port: "",
    distributor_smtp_protocol: "",
    distributor_smtp_from_address: "",
    encryption: "tls",
  };

  const handleSubmit = (values: SmtpConfigFormValues) => {
    const submissionData = {
      distributor_domain: values.distributor_domain,
      distributor_name: values.distributor_name,
      distributor_smtp_email: values.distributor_smtp_email,
      distributor_smtp_password: values.distributor_smtp_password,
      distributor_smtp_domain: values.distributor_smtp_domain,
      distributor_smtp_port: values.distributor_smtp_port,
      distributor_smtp_protocol: values.distributor_smtp_protocol,
      distributor_smtp_from_address: values.distributor_smtp_from_address,
      distributor_smtp_ssl: values.encryption === "ssl",
      distributor_smtp_tls: values.encryption === "tls",
    };

    console.log("Form submitted with values:", submissionData);
    // API call logic here
    // example: api.updateSmtpConfig(submissionData);
    navigate("/mastersettings/general-settings");
  };

  const handleClose = () => {
    navigate("/mastersettings/general-settings");
  };

  return (
    <Container fluid={true} className="py-2">
      <Row>
        <Col sm="12">
          <Formik initialValues={initialValues} onSubmit={handleSubmit}>
            {() => (
              <Form className="theme-form">
                <Card>
                  <CardHeader>
                    <h5>{t("SMTPConfiguration")}</h5>
                  </CardHeader>
                  <CardBody>
                    <Row className="g-3 d-flex align-items-center">
                      <Col md="6">
                        <FormGroup>
                          <Label htmlFor="distributor_smtp_domain">
                            {t("SMTPDomain")}
                          </Label>
                          <Field
                            id="distributor_smtp_domain"
                            name="distributor_smtp_domain"
                            type="text"
                            placeholder={t("EnterSMTPDomain")}
                            className="form-control"
                          />
                        </FormGroup>
                      </Col>

                      <Col md="6">
                        <FormGroup>
                          <Label htmlFor="distributor_name">
                            {t("DistributorName")}
                          </Label>
                          <Field
                            id="distributor_name"
                            name="distributor_name"
                            type="text"
                            placeholder={t("EnterDistributorName")}
                            className="form-control"
                          />
                        </FormGroup>
                      </Col>

                      <Col md="6">
                        <FormGroup>
                          <Label htmlFor="distributor_smtp_email">
                            {t("SMTPEmail")}
                          </Label>
                          <Field
                            id="distributor_smtp_email"
                            name="distributor_smtp_email"
                            type="email"
                            placeholder={t("EnterSMTPEmail")}
                            className="form-control"
                          />
                        </FormGroup>
                      </Col>

                      <Col md="6">
                        <PasswordInput
                          label={t("SMTPPassword")}
                          name="distributor_smtp_password"
                          id="distributor_smtp_password"
                          className="form-control"
                        />
                      </Col>

                      <Col md="6">
                        <FormGroup>
                          <Label htmlFor="distributor_smtp_port">
                            {t("SMTPPort")}
                          </Label>
                          <Field
                            id="distributor_smtp_port"
                            name="distributor_smtp_port"
                            type="text"
                            placeholder={t("EnterSMTPPort")}
                            className="form-control"
                          />
                        </FormGroup>
                      </Col>

                      <Col md="6">
                        <FormGroup>
                          <Label htmlFor="distributor_smtp_protocol">
                            {t("SMTPProtocol")}
                          </Label>
                          <Field
                            id="distributor_smtp_protocol"
                            name="distributor_smtp_protocol"
                            type="text"
                            placeholder={t("EnterSMTPProtocol")}
                            className="form-control"
                          />
                        </FormGroup>
                      </Col>

                      <Col md="6">
                        <FormGroup>
                          <Label>{t("Encryption")}</Label>
                          <div
                            role="group"
                            aria-labelledby="encryption-radio-group"
                            className="d-flex m-1"
                          >
                            <FormGroup check className="me-4">
                              <Label check>
                                <Field
                                  type="radio"
                                  name="encryption"
                                  value="ssl"
                                  className="form-check-input"
                                />
                                {t("SSL")}
                              </Label>
                            </FormGroup>
                            <FormGroup check>
                              <Label check>
                                <Field
                                  type="radio"
                                  name="encryption"
                                  value="tls"
                                  className="form-check-input"
                                />
                                {t("TLS")}
                              </Label>
                            </FormGroup>
                          </div>
                        </FormGroup>
                      </Col>

                      <Col md="6">
                        <FormGroup>
                          <Label htmlFor="distributor_smtp_from_address">
                            {t("FromAddress")}
                          </Label>
                          <Field
                            id="distributor_smtp_from_address"
                            name="distributor_smtp_from_address"
                            type="email"
                            placeholder={t("EnterFromAddress")}
                            className="form-control"
                          />
                        </FormGroup>
                      </Col>
                    </Row>
                  </CardBody>
                  <CardFooter className="d-flex justify-content-end">
                    <Button color="primary" type="submit">
                      {t("Save")}
                    </Button>
                  </CardFooter>
                </Card>
              </Form>
            )}
          </Formik>
        </Col>
      </Row>
    </Container>
  );
}

export default SMTPConfigSection;
